import styled from 'styled-components';
import { toRem, baseVariables } from '@marriott/mi-ui-library';

export interface HoverOverlayContainerProps {
  $vertical?: 'top' | 'bottom';
  $horizontal?: 'center' | 'left' | 'right';
  $markerHalfWidth?: number;
}

const getNameTransform = (
  vertical: 'top' | 'bottom',
  horizontal: 'center' | 'left' | 'right',
  markerHalfWidth: number
): string => {
  // Pill body sits in the band [marker_y - 20px, marker_y] (see HoverPricePinClone).
  // Title sits 8px above (top) or below (bottom) that band.
  const verticalOffset = vertical === 'top' ? 'calc(-100% - 28px)' : 'calc(100% + 8px)';

  switch (horizontal) {
    case 'left':
      return `translate(-${markerHalfWidth}px, ${verticalOffset})`;
    case 'right':
      return `translate(calc(-100% + ${markerHalfWidth}px), ${verticalOffset})`;
    case 'center':
    default:
      return `translate(-50%, ${verticalOffset})`;
  }
};

// Portaled into a positioned ancestor of the map (e.g. ChatMapContainer) to
// escape Google Maps' overlay pane stacking context. z-index 1400 sits below
// MSmartInfoWindow (1500). pointer-events: none so empty space doesn't trap
// mouse events; the real google.maps.Marker beneath owns hit-testing.
export const HoverOverlayPortalContainer = styled.div<{
  $markerPixelX: number;
  $markerPixelY: number;
}>`
  position: absolute;
  top: ${props => props.$markerPixelY}px;
  left: ${props => props.$markerPixelX}px;
  z-index: 1400;
  pointer-events: none;
`;

// Visual clone of `.m-map-pin:hover` from ChatMapView.styles.ts. Rendered on
// top of the real google.maps.Marker; the real marker stays the hit target.
//
// Anchor offset: Google's default Icon anchor is bottom-center, and the
// default labelOrigin is icon-center. With our 75x20 icon the label center
// sits 10px ABOVE the lat/lng pixel, so we translate by (-50%, -50% - 10px).
export const HoverPricePinClone = styled.div<{
  $aiSparkleIconUrl?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-50%, calc(-50% - ${toRem(10)}));
  background-color: ${baseVariables.color.base20};
  color: ${baseVariables.color.base10};
  border-radius: ${toRem(4)};
  box-shadow: ${baseVariables.shadows.shadowSubtle02};
  display: inline-flex;
  align-items: center;
  gap: ${toRem(2)};
  font-size: ${toRem(14)};
  line-height: 1.5;
  height: ${toRem(20)};
  padding: 0 ${toRem(4)};
  text-align: center;
  white-space: nowrap;
  font-family: var(--t-font-family, Swiss-721);
  font-weight: 500;
  cursor: pointer;
  pointer-events: none;

  &::after {
    border: ${toRem(5)} solid transparent;
    border-top-color: ${baseVariables.color.base20};
    content: '';
    left: 50%;
    margin-left: -${toRem(5)};
    position: absolute;
    top: 100%;
  }

  &.ai-recommended::before {
    content: '';
    display: inline-block;
    width: ${toRem(14)};
    height: ${toRem(14)};
    margin-right: ${toRem(2)};
    background-image: ${props => (props.$aiSparkleIconUrl ? `url('${props.$aiSparkleIconUrl}')` : 'none')};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    vertical-align: middle;
    flex-shrink: 0;
  }

  /* Single property view: the marker is a circle dot. On hover we only show the
     circle in its hover/active state (white fill, dark border) - no title text,
     no pin tail, no AI sparkle. */
  &.circle {
    width: ${toRem(18)};
    height: ${toRem(18)};
    min-width: ${toRem(18)};
    padding: 0;
    gap: 0;
    border-radius: 50%;
    background-color: ${baseVariables.color.base20};
    border: ${toRem(2)} solid ${baseVariables.color.base10};
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.4);
    color: transparent;
    font-size: 0;
    overflow: hidden;
  }

  &.circle::after,
  &.circle.ai-recommended::before {
    content: none;
    display: none;
  }
`;

export const HoverOverlayContainer = styled.div<HoverOverlayContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: max-content;
  position: absolute;
  top: 0;
  left: 0;
  transform: ${props =>
    getNameTransform(props.$vertical || 'top', props.$horizontal || 'center', props.$markerHalfWidth || 0)};
  pointer-events: none;
`;

export const PropertyNamePill = styled.div`
  background-color: var(--t-brand-bg-color, #fff);
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.4);
  max-width: 266px;
  width: max-content;

  [data-component-name='a-web-ui-kit-ALabel'] {
    color: var(--t-brand-fg-color, #1c1c1c);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    line-height: 1.2;
    max-width: 250px;
    text-align: center;
  }
`;
